/**
 * recipeHelpers.js
 *
 * Shared utilities for rendering a parse_recipe as human-readable sentences.
 * Used in MagicMapFlowModal (review stage) and ProviderTemplatesTab (read-only
 * chip in the edit modal).
 *
 * A parse_recipe has this shape (all fields optional):
 *   {
 *     sheet_name: string | null,
 *     header_row_index: number,      // 0-based; display as +1
 *     fill_down_columns: string[],
 *     required_columns: string[],
 *     drop_value_rows: { [col]: string[] },
 *     notes: string,
 *   }
 */

/**
 * buildRecipeSummaryLines(recipe)
 *
 * Returns an array of human sentence strings describing the recipe.
 * The caller renders them however suits the context (card, chip, tooltip).
 *
 * Rules:
 * - header_row_index: "Headers on row N" (1-based)
 * - fill_down_columns: "Fills down: A, B, C"
 * - drop_value_rows per column: "Drops rows where <Col> is <v1>, <v2>"
 *   When a column has only one value that reads like an aggregate label
 *   (Subtotal, Grand Total, Total) we use a friendlier phrasing:
 *   "Drops aggregate rows (<v1>, <v2>)"
 * - notes: returned as-is (displayed separately in muted text)
 *
 * @param {object} recipe
 * @returns {{ lines: string[], notes: string }}
 */
export function buildRecipeSummaryLines(recipe) {
  if (!recipe || typeof recipe !== 'object') return { lines: [], notes: '' };

  const lines = [];

  // Header row (1-based for humans)
  if (recipe.header_row_index != null) {
    lines.push(`Headers on row ${recipe.header_row_index + 1}`);
  }

  // Fill-down columns
  if (Array.isArray(recipe.fill_down_columns) && recipe.fill_down_columns.length > 0) {
    lines.push(`Fills down: ${recipe.fill_down_columns.join(', ')}`);
  }

  // Drop-value rules
  const dropRules = recipe.drop_value_rows;
  if (dropRules && typeof dropRules === 'object') {
    for (const [col, values] of Object.entries(dropRules)) {
      if (!Array.isArray(values) || values.length === 0) continue;
      // Heuristic: if ALL values look like aggregate labels, use friendly phrasing.
      const AGGREGATE_RE = /^(subtotal|grand\s*total|total|summary)$/i;
      const allAggregate = values.every((v) => AGGREGATE_RE.test(String(v).trim()));
      if (allAggregate) {
        lines.push(`Drops aggregate rows (${values.join(', ')})`);
      } else {
        // Distinguish "empty" from named values for readability.
        const formatted = values.map((v) => {
          const s = String(v);
          return s.trim() === '' ? 'empty' : s;
        });
        lines.push(`Drops rows where ${col} is ${formatted.join(', ')}`);
      }
    }
  }

  return { lines, notes: recipe.notes || '' };
}

/**
 * recipeShortLabel(recipe)
 *
 * A single-line compact description for the TemplateCard chip.
 * e.g. "headers row 3 · 2 fill-down · 3 drop rules"
 *
 * @param {object} recipe
 * @returns {string}
 */
export function recipeShortLabel(recipe) {
  if (!recipe || typeof recipe !== 'object') return '';

  const parts = [];

  if (recipe.header_row_index != null) {
    parts.push(`headers row ${recipe.header_row_index + 1}`);
  }

  const fillCount = Array.isArray(recipe.fill_down_columns)
    ? recipe.fill_down_columns.length
    : 0;
  if (fillCount > 0) {
    parts.push(`${fillCount} fill-down`);
  }

  const dropCount =
    recipe.drop_value_rows && typeof recipe.drop_value_rows === 'object'
      ? Object.keys(recipe.drop_value_rows).length
      : 0;
  if (dropCount > 0) {
    parts.push(`${dropCount} drop rule${dropCount !== 1 ? 's' : ''}`);
  }

  return parts.join(' · ');
}
